import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { Alert, Badge, Button, Drawer } from '@/shared/ui';
import type { CalendarPrimaryLegResponse, SerieLayerStateItem } from '@/api/generated/schemas';
import { LayerState, SerieLayer } from '@/api/generated/schemas';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import {
  ApproveLayerButton,
  SerieStateBadge,
  invalidateTermScopes,
  termDetailAgenda,
  useChangeTermLayerState,
  type SerieTabKey,
} from '../../../serie';
import type { CalendarEvent } from '../calendarModel';
import { TermLayerPanel } from './TermLayerPanel';
import { TermTransportSchemeEditor } from './TermTransportSchemeEditor';

function LegRow({ leg }: { leg: CalendarPrimaryLegResponse }) {
  const DirectionIcon = leg.direction === 'Outbound' ? ArrowRight : ArrowLeft;
  return (
    <div className="gap-phx-2 text-phx-sm flex items-center py-0.5">
      <DirectionIcon className="text-phx-text-muted h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="font-mono font-semibold whitespace-nowrap">
        {leg.fromCode ?? '—'}
        <span className="text-phx-text-faint mx-1">→</span>
        {leg.toCode ?? '—'}
      </span>
      <span className="text-phx-text-muted min-w-0 truncate">{leg.regularTransportName}</span>
      {leg.flightCode && (
        <Badge tone="neutral" appearance="outline">
          {leg.flightCode}
        </Badge>
      )}
    </div>
  );
}

interface TermDrawerProps {
  /** Aktivní termín, nebo `undefined` když je drawer zavřený (řídí `open`). */
  event: CalendarEvent | undefined;
  onClose: () => void;
}

/**
 * Pravý panel s rozpadem termínu: hlavička (název, rozsah, noci, stav, kapacita)
 * + blok hlavní dopravy (primary legy a generované možnosti), nebo upozornění,
 * že doprava zatím není naplánovaná. Shell (pozice/backdrop/animace) obstarává
 * sdílená komponenta `Drawer` — zde zůstává jen obsah.
 */
/** Záložka detailu termínu odpovídající dané vrstvě (index vrstvy == index záložky). */
const TAB_BY_LAYER: Partial<Record<SerieLayer, SerieTabKey>> = {
  [SerieLayer.Terms]: 'basics',
  [SerieLayer.OperationScheme]: 'operation',
  [SerieLayer.ProductCostScheme]: 'productCost',
  [SerieLayer.Calculation]: 'calculation',
};

/**
 * Panely vrstev termínu. Vlastní podkomponenta, aby se hooky (schvalování, navigace, invalidace)
 * volaly bezpodmínečně – `TermDrawer` sám může být bez aktivního termínu.
 */
function TermLayerPanels({ event }: { event: CalendarEvent }) {
  const { t } = useTranslation('venua-planner');
  const navigator = useNavigator();
  const queryClient = useQueryClient();

  const scope = {
    productId: event.productId,
    serieId: event.productSerieId,
    termId: event.termId,
  };
  const { changeState, isPending } = useChangeTermLayerState(scope);

  const openDetail = useCallback(
    (tab: SerieTabKey) => {
      // Záložka je URL stav detailu — předáváme ji jako počáteční hodnotu, dál si ji řídí sám.
      void navigator.openModal(termDetailAgenda, scope, { state: { tab } });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigator, scope.productId, scope.serieId, scope.termId]
  );

  const approveButton = (layer: SerieLayer) => (
    <ApproveLayerButton
      currentLayer={layer}
      layers={event.layers}
      serieLayers={event.serieLayers}
      onChangeState={changeState}
      loading={isPending}
      size="md"
      menuPlacement="bottom"
    />
  );

  const summaryOf = (layer: SerieLayer): string | undefined => {
    if (layer === SerieLayer.ProductCostScheme) {
      return event.inheritsProgramScheme
        ? t('calendar.drawer.layers.productCost.inherited')
        : t('calendar.drawer.layers.productCost.own');
    }
    if (layer === SerieLayer.Calculation) {
      return event.inheritsTariff
        ? t('calendar.drawer.layers.calculation.shared')
        : t('calendar.drawer.layers.calculation.own');
    }
    return undefined;
  };

  // Panel dostane každá ODEMČENÁ vrstva; zamčené se nezobrazují (čekají na předchozí vrstvu).
  const unlocked = [...event.layers]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .filter((entry: SerieLayerStateItem) => entry.state !== LayerState.Locked);

  if (unlocked.length === 0) return null;

  return (
    <>
      {unlocked.map((entry) => {
        const tab = TAB_BY_LAYER[entry.layer];
        const isTransport = entry.layer === SerieLayer.OperationScheme;
        // Doprava se v sidebaru edituje (jen autobus) – schvalovací tlačítko si vkládá do své patičky
        // vedle „Uložit"; ostatní vrstvy jsou jen ke schválení a s odkazem do detailu.
        const editableTransport = isTransport && event.transport === 'coach';

        return (
          <TermLayerPanel
            key={entry.layer}
            layerKey={
              tab === 'basics'
                ? 'terms'
                : tab === 'operation'
                  ? 'operation'
                  : tab === 'productCost'
                    ? 'productCost'
                    : 'calculation'
            }
            state={entry.state}
            summary={summaryOf(entry.layer)}
            onOpenDetail={tab ? () => openDetail(tab) : undefined}
            approveAction={editableTransport ? undefined : approveButton(entry.layer)}
          >
            {editableTransport ? (
              <TermTransportSchemeEditor
                key={event.termId}
                productId={event.productId}
                serieId={event.productSerieId}
                termId={event.termId}
                termTransport={event.termTransport}
                operationLayerState={entry.state}
                approveAction={approveButton(entry.layer)}
                onSaved={() => invalidateTermScopes(queryClient, scope)}
              />
            ) : null}
          </TermLayerPanel>
        );
      })}
    </>
  );
}

export function TermDrawer({ event, onClose }: TermDrawerProps) {
  const { t, i18n } = useTranslation('venua-planner');

  // Poslední ne-`undefined` event drží obsah vykreslený i během zavírací animace,
  // kdy už `event` je `undefined` (drawer se ještě zasouvá doprava).
  const lastEventRef = useRef<CalendarEvent | undefined>(event);
  useEffect(() => {
    if (event) lastEventRef.current = event;
  }, [event]);
  const shownEvent = event ?? lastEventRef.current;

  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });
  const schema = shownEvent?.schema;
  const outboundLegs = schema?.primaryLegs.filter((leg) => leg.direction === 'Outbound') ?? [];
  const inboundLegs = schema?.primaryLegs.filter((leg) => leg.direction === 'Inbound') ?? [];
  const hasTransport = (schema?.primaryLegs.length ?? 0) > 0;

  if (!shownEvent) return null;

  return (
    <Drawer open={event != null} onClose={onClose} ariaLabel={shownEvent.name}>
      <div
        className="border-phx-border gap-phx-2 flex items-start border-b p-4"
        data-testid="calendar-term-drawer"
      >
        <div className="min-w-0 flex-1">
          <h3 className="text-phx-lg text-phx-text truncate font-semibold">{shownEvent.name}</h3>
          <p className="text-phx-sm text-phx-text-muted">
            {dateFormat.format(shownEvent.start)} – {dateFormat.format(shownEvent.end)} ·{' '}
            {t('calendar.bar.nights', { count: shownEvent.nights })}
          </p>
          <div className="gap-phx-2 mt-1.5 flex flex-wrap items-center">
            <SerieStateBadge state={shownEvent.termLayerState} />
            <Badge tone="neutral" appearance="outline">
              {t(`calendar.transport.${shownEvent.transport}`)}
            </Badge>
            {shownEvent.capacity != null && (
              <span className="text-phx-sm text-phx-text-muted">
                {t('calendar.drawer.capacity', { count: shownEvent.capacity })}
              </span>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
          aria-label={t('calendar.drawer.close')}
          data-testid="calendar-drawer-close"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="gap-phx-3 flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
        {/* Panel pro každou odemčenou vrstvu termínu (stav termínu, doprava, product cost, kalkulace). */}
        <TermLayerPanels event={shownEvent} />

        {/* Rozpad dopravy u leteckých / kombinovaných termínů, kde se doprava v sidebaru needituje. */}
        {shownEvent.transport !== 'coach' &&
          (!hasTransport ? (
            <Alert severity="info" title={t('calendar.drawer.noTransportTitle')}>
              {t('calendar.drawer.noTransportDescription')}
            </Alert>
          ) : (
            <>
              <section>
                <h4 className="text-phx-xs text-phx-text-2 mb-1 font-semibold tracking-wide uppercase">
                  {t('calendar.drawer.outbound')}
                </h4>
                {outboundLegs.length > 0 ? (
                  outboundLegs.map((leg) => (
                    <LegRow key={`${leg.ticketId}-${leg.facilityId}`} leg={leg} />
                  ))
                ) : (
                  <p className="text-phx-sm text-phx-text-muted italic">
                    {t('calendar.drawer.noLegs')}
                  </p>
                )}
              </section>
              <section>
                <h4 className="text-phx-xs text-phx-text-2 mb-1 font-semibold tracking-wide uppercase">
                  {t('calendar.drawer.inbound')}
                </h4>
                {inboundLegs.length > 0 ? (
                  inboundLegs.map((leg) => (
                    <LegRow key={`${leg.ticketId}-${leg.facilityId}`} leg={leg} />
                  ))
                ) : (
                  <p className="text-phx-sm text-phx-text-muted italic">
                    {t('calendar.drawer.noLegs')}
                  </p>
                )}
              </section>
            </>
          ))}
      </div>
    </Drawer>
  );
}
